import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import { Tab, Tabs } from '@material-ui/core';

import BaseDialog from 'core/components/base-dialog/base-dialog';
import { LottieLoader } from 'core/components/loading';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { useStore } from 'core/store';
import { InventoryCategory } from '../inventory-category';
import parseInventoryItems from '../../helpers/parse-inventory-items';

import useStyles from './review-inventory-images-modal.styles';

const TABS = {
  AUTO: 'AUTO',
  HOME: 'HOME',
  UMBRELLA: 'UMBRELLA',
  SPP: 'SPP'
};

const ReviewInventoryImagesModal = observer(({ open, onClose }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(TABS.AUTO);
  const { account } = useStore();
  const { inventoryItems = [], inventoryItemsLoading, fetchInventoryItems } = account;

  useEffect(() => {
    if (open && !inventoryItemsLoading && !inventoryItems.length) {
      fetchInventoryItems();
    }
  }, [fetchInventoryItems, open, inventoryItemsLoading, inventoryItems]);

  const {
    parsedItems,
    categoryItemCount,
    hasAutoImages,
    hasHomeImages,
    hasUmbrellaImages,
    hasSppImages,
    homeCategories,
    umbrellaCategories
  } = parseInventoryItems(inventoryItems);

  useEffect(() => {
    if (hasAutoImages) {
      setTab(TABS.AUTO);
    } else if (hasHomeImages) {
      setTab(TABS.HOME);
    } else if (hasUmbrellaImages) {
      setTab(TABS.UMBRELLA);
    } else if (hasSppImages) {
      setTab(TABS.SPP);
    }
  }, [hasAutoImages, hasHomeImages, hasUmbrellaImages, hasSppImages]);

  return (
    <BaseDialog open={open} onClose={onClose} classes={{ paper: classes.container }}>
      <Label type="greenBig" className={classes.modalHeader}>
        Inventory images
      </Label>
      <Tabs value={tab} variant="fullWidth" onChange={(_, newTab) => setTab(newTab)} className={classes.bottomSpacing}>
        {hasAutoImages && <Tab value={TABS.AUTO} label="Auto Images" className={classes.tab} />}
        {hasHomeImages && <Tab value={TABS.HOME} label="Home Images" className={classes.tab} />}
        {hasUmbrellaImages && <Tab value={TABS.UMBRELLA} label="Umbrella Images" className={classes.tab} />}
        {hasSppImages && <Tab value={TABS.SPP} label="SPP Images" className={classes.tab} />}
      </Tabs>

      {inventoryItemsLoading ? (
        <div className={classes.loadingContainer}>
          <LottieLoader />
        </div>
      ) : (
        <>
          {/* AUTO IMAGES */}
          {tab === TABS.AUTO && (
            <Grid xs={12} key="cars">
              <InventoryCategory category="cars" items={parsedItems.auto} itemCount={categoryItemCount.auto} />
            </Grid>
          )}

          {/* HOME IMAGES */}
          {tab === TABS.HOME &&
            homeCategories.map((category) => (
              <Grid xs={12} key={category}>
                <InventoryCategory
                  category={category}
                  items={parsedItems[category]}
                  itemCount={categoryItemCount[category]}
                />
              </Grid>
            ))}

          {/* UMBRELLA IMAGES */}
          {tab === TABS.UMBRELLA &&
            umbrellaCategories.map((category) => (
              <Grid xs={12} key={category}>
                <InventoryCategory
                  category={category}
                  items={parsedItems[category]}
                  itemCount={categoryItemCount[category]}
                />
              </Grid>
            ))}

          {/* SPP IMAGES */}
          {tab === TABS.SPP && (
            <Grid xs={12} key="spp">
              <InventoryCategory category="spp" items={parsedItems.spp} itemCount={categoryItemCount.spp} />
            </Grid>
          )}
        </>
      )}

      <Grid className={classes.btnContainer}>
        <Button variant="contained" color="primary" className={classes.closeBtn} onClick={onClose}>
          Close
        </Button>
      </Grid>
    </BaseDialog>
  );
});

ReviewInventoryImagesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  inventoryItems: PropTypes.object.isRequired
};

export default ReviewInventoryImagesModal;
